// src/Pages/ForgotPassword.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "./styles.css";

import { auth } from "../firebaseConfig";
import { sendPasswordResetEmail } from "firebase/auth";

function ForgotPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  // Optional: localize Firebase's email
  useEffect(() => {
    try {
      auth.useDeviceLanguage?.();
    } catch {}
  }, []);

  const isValidEmail = useMemo(() => {
    const v = email.trim();
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
  }, [email]);

  async function handlePasswordReset(event) {
    event.preventDefault();
    setLoading(true);
    setError("");
    setMessage("");

    const cleaned = email.trim().toLowerCase();
    if (!cleaned || !isValidEmail) {
      setError("Please enter a valid email address.");
      setLoading(false);
      return;
    }

    try {
      await sendPasswordResetEmail(auth, cleaned);
      setMessage("Password reset email sent! Please check your inbox.");
    } catch (err) {
      // Common Firebase Auth error codes
      const map = {
        "auth/invalid-email": "Invalid email format. Please enter a valid email address.",
        "auth/user-not-found": "No user found with this email address.",
        "auth/too-many-requests": "Too many attempts. Please wait a bit and try again.",
        "auth/network-request-failed": "Network error. Check your connection and try again.",
      };
      setError(map[err?.code] || "Failed to send password reset email. Please try again later.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <nav className="navbar navbar-expand-lg navbar-light bg-light shadow">
        <a className="navbar-brand" href="#">
          <img src={require("../assets/Ecom-Icon.png")} alt="Logo" />
          <strong>EcomElevate</strong>
        </a>
      </nav>

      <div className="container-fluid vh-100 d-flex align-items-center justify-content-center">
        <div className="row w-100">
          <div
            className="col-md-6 d-none d-md-block p-5 position-relative"
            style={{ backgroundColor: "#66d1be", color: "white" }}
          >
            <div className="text-center my-5">
              <h1 className="display-4 text-white">Reset Password</h1>
              <p className="lead">
                Enter your email address and we'll send you a link to reset your password.
              </p>
            </div>
          </div>

          <div className="col-md-6 bg-light p-5">
            <h2 className="text-center mb-4">Forgot Password</h2>

            <form onSubmit={handlePasswordReset} noValidate>
              <div className="form-group">
                <label htmlFor="email">Email address</label>
                <input
                  type="email"
                  className="custom-input"
                  id="email"
                  placeholder="Enter email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                />
              </div>

              {error && <p className="text-danger">{error}</p>}
              {message && <p className="text-success">{message}</p>}

              <div className="text-center">
                <button
                  type="submit"
                  className="btn btn-primary btn-block rounded-pill py-2 px-5 w-100"
                  disabled={loading || !isValidEmail}
                >
                  {loading ? (
                    <>
                      <div className="spinner-grow" role="status" aria-hidden="true" />
                      <span className="sr-only">Loading...</span>
                    </>
                  ) : (
                    "Send Reset Link"
                  )}
                </button>
              </div>

              <div className="text-center mt-4">
                <p>
                  Remember your password?{" "}
                  <span
                    className="text-uppercase fw-bold"
                    style={{ cursor: "pointer" }}
                    onClick={() => navigate("/login")}
                  >
                    Login
                  </span>
                </p>
              </div>
            </form>

            <div className="small text-muted mt-3">
              Tip: Make sure your Firebase Auth template’s <em>Action URL</em> points to your
              <code> /reset-password </code> route. The email link will include
              <code> mode=resetPassword&oobCode=… </code>, which your ResetPassword page consumes.
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default ForgotPassword;
